import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import { Home, Search, Building2, Briefcase, Calendar, Tag, ArrowLeft, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const NotFoundPage = () => {
  const location = useLocation();
  const attemptedPath = location.pathname;

  // Quick links for navigation
  const quickLinks = [
    { title: 'Local Businesses', icon: Building2, href: '/businesses', color: 'text-amber-500' },
    { title: 'Job Opportunities', icon: Briefcase, href: '/jobs', color: 'text-emerald-500' },
    { title: 'Events', icon: Calendar, href: '/events', color: 'text-blue-500' },
    { title: 'Marketplace', icon: Tag, href: '/marketplace', color: 'text-purple-500' },
  ];

  // Popular Essex towns
  const popularTowns = [
    'Chelmsford', 'Colchester', 'Southend-on-Sea', 'Basildon', 
    'Brentwood', 'Braintree', 'Maldon', 'Clacton-on-Sea',
    'Harlow', 'Rayleigh', 'Billericay', 'Leigh-on-Sea'
  ];

  return (
    <>
      <SEO title="Page Not Found" noIndex={true} />

      <div className="min-h-[80vh] bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              {/* 404 Visual */}
              <div className="relative mb-8">
                <div className="text-[150px] lg:text-[200px] font-black text-slate-100 leading-none select-none">
                  404
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white rounded-full p-6 shadow-lg">
                    <MapPin className="h-12 w-12 text-[#0F2C3E]" />
                  </div>
                </div>
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
                Oops! This page seems to have wandered off
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                The page you're looking for doesn't exist or may have been moved. 
                But don't worry – there's plenty to discover in Essex!
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <Link to="/">
                  <Button size="lg" className="gap-2 bg-[#0F2C3E] hover:bg-[#1a3a4f]">
                    <Home className="h-5 w-5" />
                    Back to Homepage
                  </Button>
                </Link>
                <Link to="/search">
                  <Button size="lg" variant="outline" className="gap-2">
                    <Search className="h-5 w-5" />
                    Search Listings
                  </Button>
                </Link>
              </div>

              {/* Attempted Path */}
              {attemptedPath && attemptedPath !== '/' && (
                <p className="text-sm text-slate-400 mb-8">
                  Looking for: <code className="bg-slate-100 px-2 py-1 rounded">{attemptedPath}</code>
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Quick Links Section */}
        <section className="py-12 bg-white border-t border-b">
          <div className="container mx-auto px-4">
            <h2 className="text-xl font-semibold text-center text-slate-700 mb-8">
              What are you looking for?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {quickLinks.map((link) => (
                <Link key={link.href} to={link.href}>
                  <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                    <CardContent className="p-6 text-center">
                      <link.icon className={`h-10 w-10 ${link.color} mx-auto mb-3`} />
                      <p className="font-medium text-slate-700">{link.title}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Explore Towns Section */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl font-semibold text-center text-slate-700 mb-6">
                Explore Essex Towns
              </h2>
              <div className="flex flex-wrap justify-center gap-3">
                {popularTowns.map((town) => (
                  <Link 
                    key={town}
                    to={`/town/${town.toLowerCase().replace(/ /g, '-')}`}
                    className="px-4 py-2 bg-slate-100 hover:bg-[#0F2C3E] hover:text-white text-slate-700 rounded-full text-sm font-medium transition-colors"
                  >
                    {town}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Help Section */}
        <section className="py-12 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-xl font-semibold text-slate-700 mb-4">Need Help?</h2>
              <p className="text-slate-600 mb-6">
                If you believe this page should exist or you're having trouble finding what you need, 
                please get in touch with us.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/contact">
                  <Button variant="outline">Contact Us</Button>
                </Link>
                <Button 
                  variant="ghost" 
                  className="gap-2"
                  onClick={() => window.history.back()}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Go Back
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SEO Footer */}
        <section className="py-8 bg-[#0F2C3E] text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-slate-300 text-sm">
                Discover Essex is your local directory for businesses, jobs, events, and marketplace listings 
                across Essex. Connect with your community today.
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default NotFoundPage;
